import { WatchQueryFetchPolicy } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useUserManagementStore } from '@modules/users/store/hooks';
import { UserManagementActionType } from '@modules/users/store/userManagementActionType';
import { getUsersStateFromListUsersResponse } from '@modules/users/utils';
import { useListUsersQuery, UserState } from 'generated/graphql';
import { useEffect } from 'react';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { useGetUser } from 'v2/core/services/user';

interface UseFetchUsersOptions {
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
}

export const useFetchUsers = (options?: UseFetchUsersOptions) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const dispatch = useUserManagementStore(({ dispatch }) => dispatch);

  const { data, error, refetch, loading, called, networkStatus } =
    useListUsersQuery({
      variables: {
        tenantID,
        filter: {
          states: [UserState.Active, UserState.Suspended],
        },
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: options?.fetchPolicy ?? 'cache-and-network',
      skip: options?.skip,
      onError: () => {
        showErrorNotification({
          contentComponent: 'Failed to fetch users.',
          props: {},
        });
      },
    });

  const isLoading = !data && loading;

  useEffect(() => {
    if (data?.listUsers) {
      dispatch({
        payload: {
          users: getUsersStateFromListUsersResponse(data.listUsers.users),
        },
        type: UserManagementActionType.SET_USERS_DATA,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.listUsers]);

  useEffect(() => {
    dispatch({
      type: UserManagementActionType.SET_VISUAL,
      payload: { isFetchingUsers: isLoading },
    });
  }, [isLoading, dispatch]);

  const status = apolloStatusToQueryStatus(
    isLoading,
    called,
    false,
    error,
    networkStatus,
  );

  return { refetch, loading: isLoading, error, status };
};
